import { useCallback, useEffect, useRef, useState } from "react";
import { track } from "@visioni/a3s-analytics";
import { useTenantId } from "@src/features/tenants";
import {
  cameraScope,
  LivestreamStillWatchingContinued,
  LivestreamStillWatchingPrompted,
  LivestreamStillWatchingStopped,
} from "../analytics";

/**
 * Server-side stream expires after 115s. Prompt 15s before expiry so the user
 * has time to choose before the server cuts the connection.
 */
const STILL_WATCHING_PROMPT_MS = 95_000;
const STILL_WATCHING_AUTO_STOP_SECONDS = 15;

export type UseStillWatchingOptions = {
  /** Whether the stream is actively playing (not buffering, not errored) */
  isActive: boolean;
  siteId?: string;
  cameraKey: string;
  /** Called when the session should be refreshed (user chose "keep watching") */
  onRefresh: () => void;
  /** Called when the stream should be closed (user chose "stop" or auto-stopped) */
  onClose: () => void;
};

export function useStillWatching({ isActive, siteId, cameraKey, onRefresh, onClose }: UseStillWatchingOptions) {
  const tenantId = useTenantId();
  const [showPrompt, setShowPrompt] = useState(false);
  const streamActiveTimeRef = useRef<number | null>(null);

  // Prompt the user after STILL_WATCHING_PROMPT_MS of active streaming
  useEffect(() => {
    if (!isActive) return;

    streamActiveTimeRef.current = Date.now();

    const promptTimer = setTimeout(() => {
      setShowPrompt(true);
      track(LivestreamStillWatchingPrompted, {
        ...cameraScope(tenantId, siteId, cameraKey),
        active_ms: Date.now() - (streamActiveTimeRef.current ?? Date.now()),
      });
    }, STILL_WATCHING_PROMPT_MS);

    return () => clearTimeout(promptTimer);
  }, [isActive, cameraKey, tenantId, siteId]);

  // Auto-stop if the user doesn't respond within the countdown
  useEffect(() => {
    if (!showPrompt) return;

    const autoStopTimer = setTimeout(() => {
      track(LivestreamStillWatchingStopped, {
        ...cameraScope(tenantId, siteId, cameraKey),
        reason: "auto",
      });
      setShowPrompt(false);
      onClose();
    }, STILL_WATCHING_AUTO_STOP_SECONDS * 1000);

    return () => clearTimeout(autoStopTimer);
  }, [showPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueWatching = useCallback(() => {
    setShowPrompt(false);
    track(LivestreamStillWatchingContinued, cameraScope(tenantId, siteId, cameraKey));
    onRefresh();
  }, [onRefresh, cameraKey, tenantId, siteId]);

  const handleStopWatching = useCallback(() => {
    setShowPrompt(false);
    track(LivestreamStillWatchingStopped, {
      ...cameraScope(tenantId, siteId, cameraKey),
      reason: "manual",
    });
    onClose();
  }, [onClose, cameraKey, tenantId, siteId]);

  const reset = useCallback(() => {
    setShowPrompt(false);
    streamActiveTimeRef.current = null;
  }, []);

  return {
    showPrompt,
    autoStopSeconds: STILL_WATCHING_AUTO_STOP_SECONDS,
    handleContinueWatching,
    handleStopWatching,
    reset,
  };
}
